import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { CONFIG } from './config.js';
import { estiloBase, camadasImoveis, CAMADAS_INTERATIVAS } from './estilo-mapa.js';
import { registraCapsula } from './capsula.js';

const semAnimacao = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function criaMapa(elemento) {
  // PMTiles é um arquivo só, lido por requisições de intervalo pelo nginx
  maplibregl.addProtocol('pmtiles', new Protocol().tile);

  const mapa = new maplibregl.Map({
    container: elemento,
    style: estiloBase(),
    center: CONFIG.centro,
    zoom: CONFIG.zoom,
    maxBounds: [[-44, -10], [-34, -1]], // não deixa o usuário se perder no oceano
    attributionControl: { compact: true },
  });

  mapa.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');
  mapa.addControl(new maplibregl.GeolocateControl({ trackUserLocation: false }), 'top-left');

  let ativo = null;
  const pronto = new Promise((resolve) => mapa.on('load', resolve));

  pronto.then(() => {
    mapa.addSource('imoveis', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
      cluster: true,
      clusterMaxZoom: CONFIG.zoomMaxAgrupamento,
      clusterRadius: 55,
      promoteId: 'id',
    });
    // A pilula precisa existir no atlas antes da camada que a usa
    registraCapsula(mapa);
    for (const camada of camadasImoveis()) mapa.addLayer(camada);

    // Clicar num grupo aproxima até ele se abrir
    mapa.on('click', 'grupos', async (e) => {
      const f = mapa.queryRenderedFeatures(e.point, { layers: ['grupos'] })[0];
      if (!f) return;
      const zoom = await mapa.getSource('imoveis')
        .getClusterExpansionZoom(f.properties.cluster_id);
      mapa.easeTo({ center: f.geometry.coordinates, zoom });
    });

    for (const c of ['grupos', ...CAMADAS_INTERATIVAS]) {
      mapa.on('mouseenter', c, () => { mapa.getCanvas().style.cursor = 'pointer'; });
      mapa.on('mouseleave', c, () => { mapa.getCanvas().style.cursor = ''; });
    }
  });

  function marcaAtivo(id) {
    if (ativo !== null) {
      mapa.setFeatureState({ source: 'imoveis', id: ativo }, { ativo: false });
    }
    ativo = id;
    if (id !== null) mapa.setFeatureState({ source: 'imoveis', id }, { ativo: true });
  }

  return {
    mapa,
    pronto,

    async defineDados(geojson) {
      await pronto;
      mapa.getSource('imoveis').setData(geojson);
    },

    /** Chama `fn(propriedades, pontoNaTela)`, ou `fn(null)` ao sair. */
    aoPassarMouse(fn) {
      pronto.then(() => {
        mapa.on('mousemove', (e) => {
          const f = mapa.queryRenderedFeatures(e.point, { layers: CAMADAS_INTERATIVAS })[0];
          if (!f) { marcaAtivo(null); fn(null); return; }
          marcaAtivo(f.properties.id);
          fn(f.properties, e.point);
        });
        mapa.getCanvas().addEventListener('mouseleave', () => {
          marcaAtivo(null);
          fn(null);
        });
      });
    },

    aoClicar(fn) {
      pronto.then(() => {
        for (const c of CAMADAS_INTERATIVAS) {
          mapa.on('click', c, (e) => fn(e.features[0].properties));
        }
      });
    },

    voaPara(lng, lat, zoom = 16) {
      if (semAnimacao()) mapa.jumpTo({ center: [lng, lat], zoom });
      else mapa.flyTo({ center: [lng, lat], zoom, speed: 1.4 });
    },

    enquadraCeara() {
      mapa.fitBounds(CONFIG.limitesCeara, {
        padding: 40,
        animate: !semAnimacao(),
      });
    },

    redimensiona: () => mapa.resize(),
  };
}
